// API Service para comunicarse con el backend AWS
import axios from 'axios'
import { fetchAuthSession } from 'aws-amplify/auth'

const API_BASE_URL = import.meta.env.VITE_API_GATEWAY_URL

// Obtener token de autenticación de Cognito
async function getAuthToken() {
    try {
        const session = await fetchAuthSession()
        return session.tokens?.idToken?.toString()
    } catch (error) {
        console.error('Error obteniendo token:', error)
        throw new Error('Usuario no autenticado')
    }
}

// Cliente axios configurado
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    }
})

// Interceptor para añadir token de autenticación
apiClient.interceptors.request.use(async (config) => {
    const token = await getAuthToken()
    if (token) {
        config.headers.Authorization = `Bearer ${token}`
    }
    return config
})

// ========================================
// Servicios de Upload
// ========================================

/**
 * Solicita URLs pre-firmadas para subir archivos a S3
 * @param {Array} fileTypes - Array de tipos de archivos ['token', 'libro']
 * @returns {Promise} - Respuesta con URLs de upload
 */
export async function getUploadUrls(fileTypes) {
    try {
        const response = await apiClient.post('/upload-url', { fileTypes })
        return response.data
    } catch (error) {
        console.error('Error obteniendo URLs de upload:', error)
        throw error
    }
}

/**
 * Sube un archivo directamente a S3 usando URL pre-firmada
 * @param {string} uploadUrl - URL pre-firmada de S3
 * @param {File} file - Archivo a subir
 * @returns {Promise}
 */
export async function uploadFileToS3(uploadUrl, file) {
    try {
        // IMPORTANTE: El Content-Type debe coincidir EXACTAMENTE con el usado en el presigned URL
        // Usar 'application/vnd.ms-excel' que es genérico y acepta .xls y .xlsx
        await axios.put(uploadUrl, file, {
            headers: {
                'Content-Type': 'application/vnd.ms-excel'
            }
        })
    } catch (error) {
        console.error('Error subiendo archivo a S3:', error)
        throw error
    }
}

// ========================================
// Servicio: Comparador Token DIAN
// ========================================

/**
 * Procesa comparación entre Token DIAN y Libro Auxiliar
 * @param {Object} params
 * @param {string} params.tokenKey - Key de S3 del Token DIAN
 * @param {string} params.libroKey - Key de S3 del Libro Auxiliar
 * @returns {Promise} - Resultado con estadísticas y URL de descarga
 */
export async function procesarComparadorDian({ tokenKey, libroKey }) {
    try {
        const response = await apiClient.post('/comparador-dian', {
            tokenKey,
            libroKey
        })
        return response.data
    } catch (error) {
        console.error('Error procesando comparador DIAN:', error)
        throw error
    }
}

// ========================================
// Workflow Completo: Upload + Procesamiento
// ========================================

/**
 * Workflow completo para Comparador DIAN:
 * 1. Obtiene URLs de upload
 * 2. Sube archivos a S3
 * 3. Procesa comparación
 * 4. Retorna resultado
 */
export async function comparadorDianWorkflow({ tokenFile, auxiliarFile, nombreEmpresa }) {
    try {
        // 1. Solicitar URLs de upload
        console.log('Solicitando URLs de upload...')
        const uploadData = await getUploadUrls(['token', 'libro'])

        console.log('URLs obtenidas:', uploadData)

        const tokenUploadInfo = uploadData.files.token
        const libroUploadInfo = uploadData.files.libro

        // 2. Subir archivos a S3 en paralelo
        console.log('Subiendo archivos a S3...')
        await Promise.all([
            uploadFileToS3(tokenUploadInfo.url, tokenFile),
            uploadFileToS3(libroUploadInfo.url, auxiliarFile)
        ])

        // 3. Procesar comparación
        console.log('Procesando comparación...')
        const result = await procesarComparadorDian({
            tokenKey: tokenUploadInfo.key,
            libroKey: libroUploadInfo.key
        })

        return result
    } catch (error) {
        console.error('Error en workflow Comparador DIAN:', error)
        throw error
    }
}

// ========================================
// Servicio: Conciliación Bancaria
// ========================================

/**
 * Procesa conciliación bancaria con extracto y libro auxiliar
 * @param {Object} params - Parámetros de conciliación
 * @param {string} params.extractoKey - Key S3 del extracto bancario
 * @param {string} params.auxiliarKey - Key S3 del libro auxiliar
 * @param {string} params.banco - Banco seleccionado
 * @param {boolean} params.invertirSignos - Si invertir signos del extracto
 * @param {number} params.maxCombinaciones - Máximo de combinaciones a probar
 * @returns {Promise} - Resultado de conciliación con URLs de descarga
 */
export async function procesarConciliacionBancaria({ extractoKey, auxiliarKey, banco, invertirSignos, maxCombinaciones = 1000 }) {
    try {
        const response = await apiClient.post('/conciliacion-bancaria', {
            extractoKey,
            auxiliarKey,
            banco,
            invertirSignos,
            maxCombinaciones
        })
        return response.data
    } catch (error) {
        console.error('Error procesando conciliación bancaria:', error)
        throw error
    }
}

/**
 *Workflow completo de conciliación bancaria: upload + procesamiento
 * @param {Object} params - Parámetros del workflow
 * @param {File} params.extractoFile - Archivo de extracto bancario
 * @param {File} params.auxiliarFile - Archivo de libro auxiliar
 * @param {string} params.banco - Banco seleccionado
 * @param {boolean} params.invertirSignos - Si invertir signos
 * @param {number} params.maxCombinaciones - Máximo de combinaciones a probar
 * @returns {Promise} - Resultado completo del workflow
 */
export async function conciliacionBancariaWorkflow({ extractoFile, auxiliarFile, banco, invertirSignos, maxCombinaciones = 1000 }) {
    try {
        // 1. Solicitar URLs de upload
        console.log('Solicitando URLs de upload...')
        const uploadData = await getUploadUrls(['extracto', 'auxiliar'])

        console.log('URLs obtenidas:', uploadData)

        const extractoUploadInfo = uploadData.files.extracto
        const auxiliarUploadInfo = uploadData.files.auxiliar

        // 2. Subir archivos a S3 en paralelo
        console.log('Subiendo archivos a S3...')
        await Promise.all([
            uploadFileToS3(extractoUploadInfo.url, extractoFile),
            uploadFileToS3(auxiliarUploadInfo.url, auxiliarFile)
        ])

        // 3. Procesar conciliación
        console.log('Procesando conciliación bancaria...')
        const result = await procesarConciliacionBancaria({
            extractoKey: extractoUploadInfo.key,
            auxiliarKey: auxiliarUploadInfo.key,
            banco,
            invertirSignos,
            maxCombinaciones
        })

        return result
    } catch (error) {
        console.error('Error en workflow Conciliación Bancaria:', error)
        throw error
    }
}

// ========================================
// Servicio: List User Files from S3
// ========================================

/**
 * Lista archivos procesados del usuario en S3
 * @param {number} days - Número de días a consultar (default: 30)
 * @returns {Promise} - Lista de archivos con URLs de descarga
 */
export async function listUserFiles(days = 30) {
    try {
        const response = await apiClient.post('/list-files', { days })
        return response.data
    } catch (error) {
        console.error('Error listando archivos:', error)
        throw error
    }
}

// ========================================
// Exportaciones
// ========================================

const apiService = {
    // Upload utilities
    getUploadUrls,
    uploadFileToS3,

    // Comparador DIAN
    procesarComparadorDian,
    comparadorDianWorkflow,

    // Conciliación Bancaria
    procesarConciliacionBancaria,
    conciliacionBancariaWorkflow,

    // List Files
    listUserFiles,
}

export default apiService
